import { call, put, takeLatest, delay, select } from "redux-saga/effects";
import type { PayloadAction } from "@reduxjs/toolkit";
import {
  sendOTPRequest,
  sendOTPSuccess,
  sendOTPFailure,
  verifyOTPRequest,
  verifyOTPSuccess,
  verifyOTPFailure,
  resendOTPRequest,
  checkAuthStatusRequest,
  checkAuthStatusSuccess,
  checkAuthStatusFailure,
  createStoreRequest,
  createStoreSuccess,
  createStoreFailure,
  authorizeStoreRequest,
  authorizeStoreFailure,
  setCurrentStore,
} from "@/store/slices/auth.slice";
import * as authService from "@/services/authService";
import type { RootState } from "@/store/store";
import type { AccessRole, AuthStatusTrigger, UserRole } from "@/types/auth";

const getAuthState = (state: RootState) => state.auth;

const derivePendingRoute = (
  trigger: AuthStatusTrigger | null | undefined,
  stores: AccessRole[],
  currentStore: AccessRole | null,
) => {
  switch (trigger) {
    case "post-verify":
      return stores.length > 0 ? "/auth/store-selector" : "/auth/new-store";
    case "selector-load":
      return stores.length === 0 ? "/auth/new-store" : null;
    case "post-create-store":
      return "/auth/store-selector";
    case "post-authorize":
      if (currentStore) {
        return `/${currentStore.storeType}/${currentStore.storeId}/dashboard`;
      }
      return "/auth/store-selector";
    case "manual-retry":
      return stores.length > 0 ? "/auth/store-selector" : "/auth/new-store";
    case "new-store-load":
    default:
      return null;
  }
};

function* sendOTPSaga(
  action: PayloadAction<{
    phoneNumber: string;
    inviteId?: string;
    channel?: string;
  }>,
): Generator<unknown, void, unknown> {
  try {
    const { phoneNumber, inviteId, channel } = action.payload;
    const cleanPhone = phoneNumber.replace(/\s/g, "");

    if (cleanPhone.length !== 10 || !/^\d{10}$/.test(cleanPhone)) {
      throw new Error("Please enter a valid 10-digit phone number");
    }

    const formattedPhone = `91${cleanPhone}`;
    const response = (yield call(
      authService.sendOTP,
      formattedPhone,
      inviteId,
      channel,
    )) as authService.SendOTPResponse;

    if (!response.valid) {
      yield put(sendOTPFailure(response.message || "Failed to send OTP"));
      return;
    }

    yield put(sendOTPSuccess({ authenticationId: response.authenticationId }));
  } catch (error: unknown) {
    yield put(
      sendOTPFailure(
        error instanceof Error ? error.message : "Failed to send OTP",
      ),
    );
  }
}

function* verifyOTPSaga(
  action: PayloadAction<{ otp: string }>,
): Generator<unknown, void, unknown> {
  try {
    const { otp } = action.payload;
    const authState = (yield select(getAuthState)) as ReturnType<
      typeof getAuthState
    >;

    if (!authState.authenticationId) {
      throw new Error("Authentication ID not found. Please request OTP again.");
    }

    const response = (yield call(
      authService.verifyOTP,
      authState.authenticationId,
      otp,
    )) as authService.VerifyOTPResponse;

    if (!response.valid) {
      yield put(verifyOTPFailure(response.message || "Invalid OTP"));
      return;
    }

    const roles: UserRole[] = response.stores.map((store) => ({
      role: store.role,
      storeId: store.storeId,
      storeType: store.storeType,
      onboardingCompleted: store.onboardingCompleted,
    }));

    yield put(
      verifyOTPSuccess({
        userId: response.userId,
        roles,
        newUser: response.newUser,
      }),
    );
    yield put(checkAuthStatusRequest({ trigger: "post-verify" }));
  } catch (error: unknown) {
    yield put(
      verifyOTPFailure(error instanceof Error ? error.message : "Invalid OTP"),
    );
  }
}

function* resendOTPSaga(): Generator<unknown, void, unknown> {
  try {
    const authState = (yield select(getAuthState)) as ReturnType<
      typeof getAuthState
    >;
    const { phoneNumber, channel } = authState;

    if (!phoneNumber) {
      throw new Error("Phone number not found. Please start over.");
    }

    yield delay(1000);

    const cleanPhone = phoneNumber.replace(/\s/g, "");
    const formattedPhone = `91${cleanPhone}`;
    const response = (yield call(
      authService.sendOTP,
      formattedPhone,
      undefined,
      channel || "whatsapp",
    )) as authService.SendOTPResponse;

    if (!response.valid) {
      yield put(sendOTPFailure(response.message || "Failed to resend OTP"));
      return;
    }

    yield put(sendOTPSuccess({ authenticationId: response.authenticationId }));
  } catch (error: unknown) {
    yield put(
      sendOTPFailure(
        error instanceof Error ? error.message : "Failed to resend OTP",
      ),
    );
  }
}

function* checkAuthStatusSaga(
  action: PayloadAction<{ trigger?: AuthStatusTrigger } | undefined>,
): Generator<unknown, void, unknown> {
  const trigger = action.payload?.trigger || null;

  try {
    let response = (yield call(
      authService.getAuthStatus,
    )) as authService.StatusResponse;

    if (!response.valid) {
      yield delay(400);
      response = (yield call(
        authService.getAuthStatus,
      )) as authService.StatusResponse;
    }

    if (!response.valid) {
      yield put(
        checkAuthStatusFailure({
          error: response.message || "Failed to check auth status",
          trigger,
          attemptCount: 2,
        }),
      );
      return;
    }

    const authState = (yield select(getAuthState)) as ReturnType<
      typeof getAuthState
    >;
    const adminAuth = response.userId
      ? {
          userId: response.userId,
          phone: response.phoneNumber || "",
        }
      : undefined;

    yield put(
      checkAuthStatusSuccess({
        isAuthorized: response.valid,
        adminAuth,
        verifiedStores: response.stores || [],
        trigger,
        pendingRoute: derivePendingRoute(
          trigger,
          response.stores || [],
          authState.currentStoreInfo,
        ),
      }),
    );
  } catch (error: unknown) {
    yield put(
      checkAuthStatusFailure({
        error:
          error instanceof Error
            ? error.message
            : "Failed to check auth status",
        trigger,
        attemptCount: 2,
      }),
    );
  }
}

function* createStoreSaga(
  action: PayloadAction<authService.CreateNewStoreRequest>,
): Generator<unknown, void, unknown> {
  try {
    yield call(authService.createNewStore, action.payload);
    yield put(createStoreSuccess());
    yield put(checkAuthStatusRequest({ trigger: "post-create-store" }));
  } catch (error: unknown) {
    yield put(
      createStoreFailure(
        error instanceof Error
          ? error.message
          : "Network error occurred while creating store",
      ),
    );
  }
}

function* authorizeStoreSaga(
  action: PayloadAction<AccessRole>,
): Generator<unknown, void, unknown> {
  try {
    const store = action.payload;
    const loginResponse = (yield call(
      authService.loginToStore,
      store.storeId,
    )) as authService.LoginToStoreResponse;

    if (!loginResponse.valid) {
      yield put(
        authorizeStoreFailure(
          loginResponse.message || "Failed to login to store",
        ),
      );
      return;
    }

    let statusResponse = (yield call(
      authService.getAuthStatus,
    )) as authService.StatusResponse;

    if (!statusResponse.valid) {
      yield delay(400);
      statusResponse = (yield call(
        authService.getAuthStatus,
      )) as authService.StatusResponse;
    }

    if (!statusResponse.valid) {
      yield put(
        authorizeStoreFailure(
          statusResponse.message || "Failed to fetch store status",
        ),
      );
      return;
    }

    const matchedStore =
      statusResponse.stores.find(
        (candidate) =>
          candidate.storeId === store.storeId &&
          candidate.storeType === store.storeType,
      ) ||
      loginResponse.stores.find(
        (candidate) =>
          candidate.storeId === store.storeId &&
          candidate.storeType === store.storeType,
      ) ||
      store;

    const selectedStore: AccessRole = {
      ...matchedStore,
      authorized: true,
    };

    const verifiedStores = statusResponse.stores.some(
      (candidate) =>
        candidate.storeId === selectedStore.storeId &&
        candidate.storeType === selectedStore.storeType,
    )
      ? statusResponse.stores.map((candidate) =>
          candidate.storeId === selectedStore.storeId &&
          candidate.storeType === selectedStore.storeType
            ? { ...candidate, authorized: true }
            : candidate,
        )
      : [...statusResponse.stores, selectedStore];

    yield put(setCurrentStore(selectedStore));
    yield put(
      checkAuthStatusSuccess({
        isAuthorized: true,
        adminAuth: statusResponse.userId
          ? {
              userId: statusResponse.userId,
              phone: statusResponse.phoneNumber || "",
            }
          : undefined,
        verifiedStores,
        trigger: "post-authorize",
        pendingRoute: `/${selectedStore.storeType}/${selectedStore.storeId}/dashboard`,
      }),
    );
  } catch (error: unknown) {
    yield put(
      authorizeStoreFailure(
        error instanceof Error ? error.message : "Failed to login to store",
      ),
    );
  }
}

export function* authSaga() {
  yield takeLatest(sendOTPRequest.type, sendOTPSaga);
  yield takeLatest(verifyOTPRequest.type, verifyOTPSaga);
  yield takeLatest(resendOTPRequest.type, resendOTPSaga);
  yield takeLatest(checkAuthStatusRequest.type, checkAuthStatusSaga);
  yield takeLatest(createStoreRequest.type, createStoreSaga);
  yield takeLatest(authorizeStoreRequest.type, authorizeStoreSaga);
}
